import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mobix_site_token";

const TXT = {
  bs: {
    kicker: "Privatni pristup",
    title: "Dobrodošli u MOBIX",
    subtitle: "Ova stranica je zaštićena. Unesite lozinku za pristup.",
    placeholder: "Lozinka",
    submit: "Uđi",
    checking: "Provjera pristupa…",
    error: "Netačna lozinka. Pokušajte ponovo.",
  },
  en: {
    kicker: "Private access",
    title: "Welcome to MOBIX",
    subtitle: "This site is protected. Enter the password to continue.",
    placeholder: "Password",
    submit: "Enter",
    checking: "Checking access…",
    error: "Incorrect password. Please try again.",
  },
};

export default function AuthGate({ children }) {
  const { lang } = useLang();
  const t = TXT[lang] || TXT.bs;
  const [status, setStatus] = useState("checking"); // checking | authed | unauthed
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setStatus("unauthed");
      return;
    }
    axios
      .get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setStatus("authed"))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setStatus("unauthed");
      });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setStatus("authed");
    } catch {
      setError(t.error);
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  if (status === "authed") return children;

  if (status === "checking") {
    return (
      <div className="fixed inset-0 z-[999] flex items-center justify-center bg-mobix-navy" data-testid="auth-checking">
        <Loader2 className="h-8 w-8 animate-spin text-mobix-orange" />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-mobix-navy px-6"
      data-testid="auth-gate"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-mobix-orange/10" />
      <div className="pointer-events-none absolute -left-28 -bottom-28 h-80 w-80 rounded-full bg-white/[0.04]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10"
      >
        <div className="flex items-center gap-3">
          <img src="/mobix-favicon.png" alt="MOBIX" className="h-10 w-10" />
          <span className="text-2xl font-extrabold tracking-tight text-white">
            MOBIX<span className="text-mobix-orange">.</span>
          </span>
        </div>

        <p className="mt-9 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-mobix-orange">
          <Lock size={13} /> {t.kicker}
        </p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-white">{t.title}</h1>
        <p className="mt-3 text-sm font-medium leading-relaxed text-white/60">{t.subtitle}</p>

        <form onSubmit={submit} className="mt-8">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t.placeholder}
            autoFocus
            data-testid="auth-password-input"
            className="w-full rounded-3xl border border-white/15 bg-white/[0.06] px-6 py-4 text-white placeholder:text-white/40 outline-none transition-colors duration-300 focus:border-mobix-orange/60 focus:bg-white/[0.1]"
          />

          {error && (
            <p className="mt-3 text-sm font-medium text-mobix-orange" data-testid="auth-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-3xl bg-mobix-orange px-8 py-4 font-semibold text-white shadow-lg shadow-mobix-orange/25 transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                {t.submit}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
